import { metricKeys } from '@pulse/metrics';
import {
  AUDIENCE_METRICS,
  WIDGET_TYPES,
} from '@components/top-widget-panel/spi-panel/widgets/constants';
import { DEFAULT_WIDGETS, SUMMARY_PANEL_TYPE } from '@components/top-widget-panel/constants';
import calcShortcutInterval from '@utils/calcShortcutInterval';
import { AD_PLAYS, DATE_SHORTCUTS } from '@utils/constants';
import { ALL_TRAFFIC } from '@utils/filter';
import getGranularity from '@utils/granularity';
import { getValidPresetKeys } from '@utils/presets/utils';

const PRESET_KEYS = getValidPresetKeys();

const calIntervalObjFromShortcut = (shortcut) => {
  const [from, to] = calcShortcutInterval(shortcut);
  return {
    shortcut,
    from,
    to,
    granularity: getGranularity({ from, to, minuteGranularity: true, overview: false, shortcut }),
  };
};

const getEIUsecases = ({ validateMetric, validateDimension }) => ({
  [PRESET_KEYS.EXEC_OVERVIEW]: {
    title: 'Executive Overview',
    key: PRESET_KEYS.EXEC_OVERVIEW,
    hide: false,
    description: 'Track Concurrent Plays, SPI, Playing Time and Ad Impressions.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.StreamingPerformanceIndex),
        metrics: validateMetric([
          metricKeys.ConcurrentPlays,
          metricKeys.StreamingPerformanceIndex,
          metricKeys.IntervalMinutesPlayed,
          metricKeys.AdPlays,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'OBJECT_NAME',
            'm3.dv.n',
            'c3.video.isLive',
            'GEO_COUNTRY',
          ]),
          globalOrderBy: validateMetric(metricKeys.SpiStreams),
          dimensionMetrics: validateMetric([metricKeys.SpiStreams]),
        },
      },
      summaryWidgets: {
        type: SUMMARY_PANEL_TYPE.SPI,
        widgets: [
          {
            widgetKey: WIDGET_TYPES.audienceMetrics.name,
            enabled: true,
            config: {
              metrics: validateMetric([
                metricKeys.Plays,
                metricKeys.ConcurrentPlays,
                metricKeys.AdPlays,
              ]),
            },
          },
        ],
      },
    },
  },
  [PRESET_KEYS.EXPERIENCE_OVERVIEW]: {
    title: 'Experience Overview',
    key: PRESET_KEYS.EXPERIENCE_OVERVIEW,
    hide: false,
    description: 'Analyze SPI and understand the top Experience Improvement areas.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.StreamingPerformanceIndex),
        metrics: validateMetric([
          metricKeys.ConcurrentPlays,
          metricKeys.StreamingPerformanceIndex,
          metricKeys.VideoStartFailuresTech,
          metricKeys.HighExitBeforeVideoStart,
          metricKeys.HighRebuffering,
          metricKeys.HighStartupTime,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.LowBitrate,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'OBJECT_NAME',
            'm3.dv.n',
            'c3.video.isLive',
            'GEO_COUNTRY',
          ]),
          globalOrderBy: validateMetric(metricKeys.SpiStreams),
          dimensionMetrics: validateMetric([metricKeys.SpiStreams]),
        },
      },
    },
  },
  [PRESET_KEYS.ENGAGEMENT_OVERVIEW]: {
    title: 'Engagement Overview',
    key: PRESET_KEYS.ENGAGEMENT_OVERVIEW,
    hide: false,
    description: 'Track Audience and Engagement.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.EndedPlays),
        metrics: validateMetric([
          metricKeys.EndedPlays,
          metricKeys.UniqueDevices,
          metricKeys.Minutes,
          metricKeys.MinutesPerEndedPlay,
          metricKeys.MinutesPerUniqueDevice,
          metricKeys.PercentageComplete,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'OBJECT_NAME',
            'm3.dv.n',
            'c3.video.isLive',
            'GEO_COUNTRY',
          ]),
          globalOrderBy: validateMetric(metricKeys.Plays),
          dimensionMetrics: validateMetric([metricKeys.Plays]),
        },
      },
      summaryWidgets: {
        type: SUMMARY_PANEL_TYPE.SPI,
        widgets: [
          {
            widgetKey: WIDGET_TYPES.audienceMetrics.name,
            enabled: true,
            config: {
              metrics: validateMetric([
                metricKeys.Plays,
                metricKeys.ConcurrentPlays,
                metricKeys.IntervalMinutesPlayed,
                metricKeys.AdPlays,
              ]),
            },
          },
          ...DEFAULT_WIDGETS.SPI.filter(
            (widget) => widget.widgetKey !== WIDGET_TYPES.audienceMetrics.name,
          ).map((widget) => ({ ...widget, enabled: false })),
        ],
      },
    },
  },

  [PRESET_KEYS.DEVICE_PERFORMANCE]: {
    title: 'Device Performance',
    key: PRESET_KEYS.DEVICE_PERFORMANCE,
    hide: false,
    description:
      'Select a Device from the Dimension list to understand its performance by app version, device models, operating system version...',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.StreamingPerformanceIndex),
        metrics: validateMetric([
          metricKeys.StreamingPerformanceIndex,
          metricKeys.VideoStartFailuresTech,
          metricKeys.HighExitBeforeVideoStart,
          metricKeys.HighRebuffering,
          metricKeys.HighStartupTime,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.LowBitrate,
          metricKeys.VideoStartTime,
          metricKeys.ConnectionInducedRebufferingRatio,
          metricKeys.Bitrate,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'OBJECT_NAME',
            'm3.dv.n',
            'appVersion',
            'm3.dv.mrk',
            'm3.dv.os',
            'm3.dv.osv',
            'm3.dv.br',
            'm3.dv.brv',
          ]),
          globalOrderBy: validateMetric(metricKeys.SpiStreams),
          dimensionMetrics: validateMetric([metricKeys.SpiStreams]),
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
      },
    },
  },

  [PRESET_KEYS.ERROR_ANALYSIS]: {
    title: 'Error Analysis',
    key: PRESET_KEYS.ERROR_ANALYSIS,
    hide: false,
    description: 'Analyze Startup and Playback errors.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.Attempts),
        metrics: validateMetric([
          metricKeys.Attempts,
          metricKeys.VideoStartFailuresTech,
          metricKeys.VideoStartFailuresBusiness,
          metricKeys.EndedPlays,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.VideoPlaybackFailuresBusiness,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'OBJECT_NAME',
            'm3.dv.n',
            'appVersion',
            'c3.video.isLive',
            'GEO_COUNTRY',
          ]),
          dimensionMetrics: validateMetric([
            metricKeys.Attempts,
            metricKeys.VideoStartFailuresTech,
            metricKeys.VideoPlaybackFailuresTech,
            metricKeys.EndedPlays,
          ]),
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
      },
    },
  },

  [PRESET_KEYS.CDN_PERFORMANCE]: {
    title: 'CDN Performance',
    key: PRESET_KEYS.CDN_PERFORMANCE,
    hide: false,
    description:
      'Select a CDN from the Dimension list to understand its performance across different Geo and ISP metadata...',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.ConnectionInducedRebufferingRatio),
        metrics: validateMetric([
          metricKeys.IntervalMinutesPlayed,
          metricKeys.ConcurrentPlays,
          metricKeys.ConnectionInducedRebufferingRatio,
          metricKeys.VideoStartFailuresTech,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.VideoStartTime,
          metricKeys.Bitrate,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'OBJECT_NAME',
            'm3.dv.n',
            'CDN',
            'GEO_ASN',
            'GEO_CITY',
            'GEO_COUNTRY',
          ]),
          globalOrderBy: validateMetric(metricKeys.Plays),
          dimensionMetrics: validateMetric([metricKeys.Plays]),
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
      },
    },
  },

  [PRESET_KEYS.ANALYZE_ALERTS]: {
    title: 'Analyze Alerts',
    key: PRESET_KEYS.ANALYZE_ALERTS,
    hide: false,
    description: 'All Alerts related Metrics and Default Dimensions',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.ConnectionInducedRebufferingRatio),
        metrics: validateMetric([
          metricKeys.ConcurrentPlays,
          metricKeys.VideoStartFailuresTech,
          metricKeys.ExitBeforeVideoStarts,
          metricKeys.VideoStartTime,
          metricKeys.ConnectionInducedRebufferingRatio,
          metricKeys.VideoPlaybackFailuresTech,
          metricKeys.HighRebuffering,
          metricKeys.HighStartupTime,
          metricKeys.HighExitBeforeVideoStart,
          metricKeys.BadSession,
          metricKeys.AdRebufferingRatio,
          metricKeys.AdVideoStartFailures,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
        interval: calIntervalObjFromShortcut(DATE_SHORTCUTS.past7Days),
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'c3.player.name',
            'm3.dv.osf',
            'c3.video.isLive',
            'OBJECT_NAME',
            'CDN',
            'GEO_ISP',
            'streamingProtocol',
            'GEO_CITY',
          ]),
          dimensionMetrics: validateMetric([
            metricKeys.Attempts,
            metricKeys.ConcurrentPlays,
            metricKeys.VideoStartFailuresTech,
            metricKeys.ExitBeforeVideoStarts,
            metricKeys.VideoStartTime,
            metricKeys.ConnectionInducedRebufferingRatio,
            metricKeys.VideoPlaybackFailuresTech,
          ]),
        },
      },
      toggles: {
        sections: {
          summary: false,
        },
        diagnostics: {
          aiAlert: true,
          manualAlert: true,
        },
      },
    },
  },
});

const getAIUsecasesForEI = ({ validateMetric, validateDimension }) => ({
  [PRESET_KEYS.AD_REACH]: {
    title: 'Ad Reach & Frequency',
    key: PRESET_KEYS.AD_REACH,
    hide: false,
    description:
      'Track Ad Frequency and Reach. Understand Ad Load and how for many Ad Impressions the Ad is watched completely. Track Ad Slates.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.AdPlays),
        metrics: validateMetric([
          metricKeys.AdPlays,
          metricKeys.AdUniqueDevices,
          metricKeys.AdTotalMinutes,
          metricKeys.AdEndedPlaysPerUniqueDevice,
          metricKeys.AdCompletedCreativePlays,
          metricKeys.AdSlateDurationPct,
          metricKeys.AdSlatePlaysPct,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'AD_OBJECT_NAME',
            'm3.dv.n',
            'c3.ad.position',
            'c3.video.isLive',
            'GEO_COUNTRY',
          ]),
          globalOrderBy: validateMetric(metricKeys.AdPlays),
          dimensionMetrics: validateMetric([metricKeys.AdPlays]),
        },
      },
      summaryWidgets: {
        type: SUMMARY_PANEL_TYPE.SPI,
        widgets: [
          {
            widgetKey: WIDGET_TYPES.audienceMetrics.name,
            enabled: true,
            config: {
              metrics: validateMetric(AUDIENCE_METRICS),
            },
          },
          ...DEFAULT_WIDGETS.SPI.filter(
            (widget) => widget.widgetKey !== WIDGET_TYPES.audienceMetrics.name,
          ).map((widget) => ({ ...widget, enabled: false })),
        ],
      },
    },
  },

  [PRESET_KEYS.AD_PERFORMANCE]: {
    title: 'Ad Performance',
    key: PRESET_KEYS.AD_PERFORMANCE,
    hide: false,
    description: 'Analyze Ad Performance and User Experience.',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.AdPlays),
        metrics: validateMetric([
          metricKeys.AdPlays,
          metricKeys.AdCompletedCreativePlays,
          metricKeys.AdBitrate,
          metricKeys.AdVideoStartFailures,
          metricKeys.AdVideoPlaybackFailures,
          metricKeys.AdRebufferingRatio,
          metricKeys.AdVideoStartTime,
          metricKeys.AdExitBeforeVideoStarts,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'AD_OBJECT_NAME',
            'm3.dv.n',
            'c3.ad.position',
            'c3.ad.technology',
          ]),
          globalOrderBy: validateMetric(metricKeys.AdPlays),
          dimensionMetrics: validateMetric([metricKeys.AdPlays]),
        },
      },
      summaryWidgets: {
        type: SUMMARY_PANEL_TYPE.SPI,
        widgets: [
          {
            widgetKey: WIDGET_TYPES.audienceMetrics.name,
            enabled: true,
            config: {
              metrics: validateMetric(AUDIENCE_METRICS),
            },
          },
          ...DEFAULT_WIDGETS.SPI.filter(
            (widget) => widget.widgetKey !== WIDGET_TYPES.audienceMetrics.name,
          ).map((widget) => ({ ...widget, enabled: false })),
        ],
      },
    },
  },
});

export const genEIUsecases = ({ validateMetric, validateDimension }) => {
  if (validateMetric([AD_PLAYS]).includes(AD_PLAYS)) {
    return {
      ...getEIUsecases({ validateMetric, validateDimension }),
      ...getAIUsecasesForEI({ validateMetric, validateDimension }),
    };
  } else {
    return getEIUsecases({ validateMetric, validateDimension });
  }
};
